import { Anchor, Box, Button, PasswordInput, Stack, Text, TextInput } from "@mantine/core";
import { IconMail, IconLock } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AuthIdentityChooser } from "../components/AuthIdentityChooser";
import { AuthShell } from "../components/AuthShell";
import { AppleSignInButton } from "../components/AppleSignInButton";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { ACCOUNT_COPY, PASSWORD_RESET_COPY } from "../constants/account";
import { AUTH_PATHS, PRIVACY_URL, TERMS_URL } from "../constants/publicUrls";
import { useAuth } from "../context/useAuth";
import { ApiError } from "../lib/apiClient";
import { resolveHomePath, roleHomePath } from "../lib/roles";
import { axioTokens } from "../theme";
import "../components/authAnimations.css";

const authLabelStyles = {
  label: {
    textTransform: "uppercase" as const,
    fontSize: "var(--axio-font-size-micro)",
    fontWeight: "var(--axio-font-weight-bold)",
    letterSpacing: "var(--axio-letter-spacing-caps)",
    color: axioTokens.muted,
    marginBottom: "var(--axio-unit-6)",
  },
};

const LOGIN_FORM_FIELD_GAP = axioTokens.spacing.xxl;

export function LoginPage() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);

    const form = useForm({
        initialValues: { email: "", password: "" },
        validate: {
            email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Enter a valid email"),
            password: (v) => (v.length > 0 ? null : "Password is required"),
        },
    });

    async function handleSubmit(values: typeof form.values) {
        setSubmitting(true);
        try {
            const loggedIn = await login(values.email, values.password);
            if (loggedIn.accountDeletionRecovered) {
                notifications.show({
                    color: "green",
                    title: ACCOUNT_COPY.deleteRecoveryTitle,
                    message: ACCOUNT_COPY.deleteRecoveryBody,
                });
            }
            navigate(await resolveHomePath(loggedIn.user));
        } catch (err) {
            const message = err instanceof ApiError ? err.message : "Something went wrong";
            notifications.show({ color: "red", title: "Login failed", message });
        } finally {
            setSubmitting(false);
        }
    }

    if (user) {
        return <Navigate to={roleHomePath(user.role)} replace />;
    }

    return (
        <AuthShell
            title="Welcome back"
            subtitle="Log in to manage your candidates and track submissions."
            footer={
                <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                        New to AxioHire?{" "}
                        <Anchor component={Link} to="/join" fw="var(--axio-font-weight-semibold)" c="violet.7" style={{ letterSpacing: "var(--axio-letter-spacing-open)" }}>
                            Create an account
                        </Anchor>
                    </Text>
                    <Text size="xs" c="dimmed">
                        <Anchor href={PRIVACY_URL} target="_blank" rel="noreferrer" c="dimmed" underline="always">
                            Privacy
                        </Anchor>
                        {" · "}
                        <Anchor href={TERMS_URL} target="_blank" rel="noreferrer" c="dimmed" underline="always">
                            Terms
                        </Anchor>
                    </Text>
                </Stack>
            }
        >
            <AuthIdentityChooser
                appleSlot={<AppleSignInButton returnPath="/login" />}
                googleSlot={<GoogleSignInButton />}
                emailOpen={emailOpen}
                onChooseEmail={() => setEmailOpen(true)}
                emailFields={
                    <form onSubmit={form.onSubmit(handleSubmit)}>
                        <Stack gap={LOGIN_FORM_FIELD_GAP}>
                                <Box className="staggerEnter premiumInput" style={{ animationDelay: "var(--axio-duration-medium)" }} data-testid="login-email">
                                    <TextInput
                                        label="Email address"
                                        placeholder="name@company.com"
                                        size="lg"
                                        required
                                        leftSection={<IconMail size="var(--axio-unit-20)" color={axioTokens.muted} />}
                                        styles={authLabelStyles}
                                        {...form.getInputProps("email")}
                                    />
                                </Box>
                                <Box className="staggerEnter premiumInput" style={{ animationDelay: "var(--axio-duration-password-delay)" }} data-testid="login-password">
                                    <PasswordInput
                                        label={
                                            <div style={{ display: "flex", justifyContent: "space-between", width: "var(--axio-percentage-full)", marginBottom: "var(--axio-unit-6)" }}>
                                                <span style={{ textTransform: "uppercase", fontSize: "var(--axio-font-size-micro)", fontWeight: "var(--axio-font-weight-bold)", letterSpacing: "var(--axio-letter-spacing-caps)", color: axioTokens.muted }}>
                                                    Password <span style={{ color: "var(--mantine-color-error)" }}>*</span>
                                                </span>
                                                <Anchor component={Link} to={AUTH_PATHS.forgotPassword} size="xs" c="violet.7">
                                                    {PASSWORD_RESET_COPY.forgotLink}
                                                </Anchor>
                                            </div>
                                        }
                                        withAsterisk={false}
                                        placeholder="Enter your password"
                                        size="lg"
                                        required
                                        leftSection={<IconLock size="var(--axio-unit-20)" color={axioTokens.muted} />}
                                        styles={{ label: { width: "var(--axio-percentage-full)" } }}
                                        {...form.getInputProps("password")}
                                    />
                                </Box>
                                <Box className="staggerEnter" style={{ animationDelay: "var(--axio-duration-slow)", marginTop: "var(--axio-unit-8)" }}>
                                    <Button
                                        type="submit"
                                        fullWidth
                                        size="lg"
                                        loading={submitting}
                                        className="ctaGlow"
                                        data-testid="login-submit"
                                        styles={{
                                            root: {
                                                height: "var(--axio-unit-48)",
                                                fontSize: "var(--axio-font-size-control)",
                                                fontWeight: "var(--axio-font-weight-semibold)",
                                                letterSpacing: "var(--axio-letter-spacing-snug)",
                                                lineHeight: "var(--axio-line-height-solid)",
                                                transition: "var(--axio-transition-cta)",
                                            },
                                            label: {
                                                height: "auto",
                                                lineHeight: "var(--axio-line-height-solid)",
                                            },
                                        }}
                                    >
                                        Sign in to your account
                                    </Button>
                                </Box>
                        </Stack>
                    </form>
                }
            />
        </AuthShell>
    );
}
